import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, type CuentaBanco } from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import { cuentasAbiertas, nombreDeCuenta } from '../lib/bancos.js';
import { useT } from '../i18n/index.js';
import { Selector } from './Campo.js';

/**
 * Which bank account (`bancos.ts`, 2026-10-10): asked only where the shop has more than one that
 * fits, so a shop with one account never sees the question, and its records still name that one.
 *
 * `moneda`: what is being paid is in córdobas, which only a córdoba account pays; or in dollars,
 * which either pays, a dollar account in its dollars. `soloDolares`: dollars that go into or come
 * out of a dollar account, as a deposit's do. Each option says its currency when there are both.
 */
export function ElegirCuenta({
  moneda = 'NIO',
  soloDolares = false,
  valor,
  onCambiar,
  etiqueta,
  ayuda,
}: {
  moneda?: 'NIO' | 'USD';
  soloDolares?: boolean;
  valor: CuentaBanco;
  onCambiar: (cuenta: CuentaBanco) => void;
  etiqueta?: string;
  ayuda?: string;
}) {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const cuentas = cuentasAbiertas(config).filter((c) =>
    soloDolares ? c.moneda === 'USD' : c.moneda === 'NIO' || moneda === 'USD',
  );
  if (cuentas.length < 2) return null;
  const ambas = cuentas.some((c) => c.moneda === 'USD') && cuentas.some((c) => c.moneda === 'NIO');
  return (
    <Selector
      etiqueta={etiqueta ?? t('bancos.cual')}
      {...(ayuda ? { ayuda } : {})}
      value={valor}
      onChange={(e) => onCambiar(e.target.value as CuentaBanco)}
      data-elegir-cuenta
    >
      {cuentas.map((c) => (
        <option key={c.cuenta} value={c.cuenta}>
          {nombreDeCuenta(config, c.cuenta, t)}
          {ambas ? ` · ${c.moneda === 'USD' ? 'US$' : 'C$'}` : ''}
        </option>
      ))}
    </Selector>
  );
}
